import { useState, useEffect } from 'react';
import { ApiLocation, fetchSensorData, SensorReadings, formatTimestamp, prepareChartData } from '../../services/api';
import { getMockOrMergedData } from '../../services/mockData';
import ParameterCard from './ParameterCard';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import LoadingIndicator from './LoadingIndicator';

// Development flags
const USE_FALLBACK_DATA = true;       // Use mock data when API data is missing
const FORCE_MOCK_DATA = true;         // Force mock data even if API works
const DEBUG_MODE = true;              // Show debug information in UI
const PREFER_LIVE_DATA = false;       // Prioritize live API data over mock data
const FORCE_LIVE_DATA = false;        // NEVER use mock data - for debugging API issues

type LiveSiteDetailsProps = {
  location: ApiLocation;
  refreshTrigger: number; // New prop to trigger data refresh
  onParameterClick: (parameter: {
    label: string;
    value: string | null;
    description: string;
    chartData?: Array<{ time: string; value: number }>;
  }) => void;
}

export default function LiveSiteDetails({ location, refreshTrigger, onParameterClick }: LiveSiteDetailsProps) {
  const [sensorData, setSensorData] = useState<SensorReadings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('Loading...');
  const [retryCount, setRetryCount] = useState(0); // Restored for auto-retry logic
  const [usingMockData, setUsingMockData] = useState(false);
  const [dataSource, setDataSource] = useState<'api' | 'mock' | 'mixed'>('api');

  // Fetch sensor data on component mount and when location changes
  useEffect(() => {
    let isMounted = true;
    let retryTimeoutId: number | null = null; // Renamed to avoid conflict, stores timeout ID
    
    async function loadSensorData() {
      try {
        if (isMounted) {
          setIsLoading(true);
          setError(null);
          setUsingMockData(false);
          setDataSource('api');
        }
        
        // Force mock data in development if needed
        if (FORCE_MOCK_DATA && !FORCE_LIVE_DATA) {
          console.log("🔄 Using FORCED mock data (FORCE_MOCK_DATA=true)");
          const mockData = getMockOrMergedData(location.ID);
          if (isMounted) {
            setSensorData(mockData);
            setIsLoading(false);
            setLastUpdated('Using forced mock data');
            setUsingMockData(true);
            setDataSource('mock');
          }
          return;
        }
        
        console.log("🔄 Attempting to fetch LIVE data from API...");
        
        // Fetch real API data
        console.log("📡 Calling fetchSensorData for location:", location.ID);
        const apiData = await fetchSensorData(location.ID);
        console.log("📦 LiveSiteDetails: Received apiData from fetchSensorData:", JSON.stringify(apiData, null, 2));
        console.log("🔍 API data keys received:", Object.keys(apiData));
        console.log("🔢 Number of sensors from API:", Object.keys(apiData).length);
        
        if (isMounted) {
          // Check if we got any API data
          if (Object.keys(apiData).length === 0) {
            console.log("❌ No sensor data received from API");
            
            if (FORCE_LIVE_DATA) {
              console.log("⚠️ FORCE_LIVE_DATA is true - not falling back to mock data");
              setError('API returned no data. Live data mode is forced so not using fallback data.');
              setLastUpdated('No data available');
              setIsLoading(false);
            } else if (USE_FALLBACK_DATA) {
              // Use mock data as fallback if API returned empty data
              console.log("🔄 Falling back to mock data");
              const mockData = getMockOrMergedData(location.ID);
              setSensorData(mockData);
              setLastUpdated('Using simulated data (API returned no data)');
              setUsingMockData(true);
              setDataSource('mock');
            } else {
              setError('No sensor data available for this location.');
              setLastUpdated('No data available');
            }
          } else {
            // If PREFER_LIVE_DATA is true, we use API data as-is without merging with mock data
            if (PREFER_LIVE_DATA) {
              console.log("Using live API data:", Object.keys(apiData));
              setSensorData(apiData);
              setDataSource('api');
              
              // Look for the most recent valid timestamp from any sensor
              const validTimestamps = Object.values(apiData)
                .map(sensor => sensor.latestValidTimestamp)
                .filter(timestamp => timestamp !== null && timestamp !== undefined);
              
              // If we have valid timestamps, use the most recent one
              if (validTimestamps.length > 0) {
                setLastUpdated(validTimestamps[0] || new Date().toLocaleString());
              } else {
                // Fallback to reading timestamps if no valid timestamps
                const timestamps = Object.values(apiData)
                  .filter(sensor => sensor.readings && sensor.readings.length > 0)
                  .flatMap(sensor => sensor.readings.map(reading => reading.timestamp));
                
                if (timestamps.length > 0) {
                  const mostRecent = timestamps.sort((a, b) => 
                    new Date(b).getTime() - new Date(a).getTime()
                  )[0];
                  setLastUpdated(formatTimestamp(mostRecent));
                } else {
                  setLastUpdated(new Date().toLocaleString());
                }
              }
            } else if (USE_FALLBACK_DATA) {
              // If we have some API data but it might be incomplete, merge with mock data
              const mergedData = getMockOrMergedData(location.ID, apiData);
              setSensorData(mergedData);
              
              // Check if we're using any mock data
              const usingAnyMock = Object.keys(mergedData).length > Object.keys(apiData).length;
              setUsingMockData(usingAnyMock);
              setDataSource(usingAnyMock ? 'mixed' : 'api');
              
              if (usingAnyMock) {
                setLastUpdated('Using partially simulated data');
              } else {
                // Get the most recent timestamp from any sensor
                const allTimestamps = Object.values(apiData)
                  .filter(sensor => sensor.readings && sensor.readings.length > 0)
                  .map(sensor => sensor.readings[0].timestamp);
                
                if (allTimestamps.length > 0) {
                  const mostRecent = allTimestamps.sort((a, b) => 
                    new Date(b).getTime() - new Date(a).getTime()
                  )[0];
                  setLastUpdated(formatTimestamp(mostRecent));
                } else {
                  setLastUpdated('No timestamp available');
                }
              }
            } else {
              setSensorData(apiData);
              setDataSource('api');
              
              // Get the most recent timestamp from any sensor
              const allTimestamps = Object.values(apiData)
                .filter(sensor => sensor.readings && sensor.readings.length > 0)
                .map(sensor => sensor.readings[0].timestamp);
              
              if (allTimestamps.length > 0) {
                const mostRecent = allTimestamps.sort((a, b) => 
                  new Date(b).getTime() - new Date(a).getTime()
                )[0];
                setLastUpdated(formatTimestamp(mostRecent));
              } else {
                setLastUpdated('No timestamp available');
              }
            }
          }
          
          setIsLoading(false);
          // Reset auto-retry count on successful data load
          setRetryCount(0);
        }
      } catch (err) {
        console.error('❌ Error fetching sensor data:', err);
        
        if (isMounted) {
          if (FORCE_LIVE_DATA) {
            console.log("⚠️ FORCE_LIVE_DATA is true - not falling back to mock data despite API error");
            setError(`API error: ${err instanceof Error ? err.message : 'Unknown error'}`);
            setLastUpdated('Error fetching data');
            setIsLoading(false);
          } else if (USE_FALLBACK_DATA) {
            // Use mock data if API call fails
            console.log("🔄 API call failed, falling back to mock data");
            const mockData = getMockOrMergedData(location.ID);
            setSensorData(mockData);
            setIsLoading(false);
            setLastUpdated('Using simulated data (API error)');
            setUsingMockData(true);
            setDataSource('mock');
          } else {
            setError('Failed to load sensor data. Please try again later.');
            setIsLoading(false);
            
            // Auto-retry up to 3 times with exponential backoff
            if (retryCount < 3) { // Correctly use retryCount state
              const nextRetryDelay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
              console.log(`🔄 Retrying in ${nextRetryDelay}ms (attempt ${retryCount + 1}/3)`);
              
              retryTimeoutId = window.setTimeout(() => { // Store timeout ID
                setRetryCount(prev => prev + 1); // Correctly use setRetryCount
              }, nextRetryDelay);
            }
          }
        }
      }
    }
    
    loadSensorData();
    
    // Set up polling for real-time updates (every 30 seconds)
    const interval = setInterval(loadSensorData, 30000);
    
    return () => {
      isMounted = false;
      clearInterval(interval);
      if (retryTimeoutId) window.clearTimeout(retryTimeoutId); // Clear timeout using its ID
    };
  }, [location.ID, refreshTrigger, retryCount]); // Added refreshTrigger and existing retryCount

  // Create static coordinate parameters
  const coordinateParameters = [
    { 
      label: 'LONGITUDE', 
      value: location.gCoord.y.toFixed(4) + '°', 
      description: 'Geographic coordinate' 
    },
    { 
      label: 'LATITUDE', 
      value: location.gCoord.x.toFixed(4) + '°', 
      description: 'Geographic coordinate' 
    }
  ];

  // Create temperature chart data if available
  const temperatureReadings = sensorData?.TEMPERATURE?.readings || [];
  const temperatureChartData = temperatureReadings.length > 0 
    ? prepareChartData(temperatureReadings)
    : [];

  // Toggle between forced mock and api data
  const toggleMockData = () => {
    // This is only for development, toggle the FORCE_MOCK_DATA flag in the code for production
    // Trigger a re-fetch by updating retryCount, which is in useEffect's dependency array
    setRetryCount(prev => prev + 1); 
  };

  // Get badge color based on data source
  const getDataSourceBadge = () => {
    switch(dataSource) {
      case 'api':
        return { 
          bg: 'bg-green-100', 
          text: 'text-green-800', 
          label: 'Live Data',
          isLive: true // Add a flag for live data
        };
      case 'mock':
        return { bg: 'bg-red-100', text: 'text-red-800', label: 'MOCK DATA', isLive: false };
      case 'mixed':
        return { bg: 'bg-orange-100', text: 'text-orange-800', label: 'PARTIAL MOCK DATA', isLive: false };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Unknown', isLive: false };
    }
  };

  const badge = getDataSourceBadge();

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h2 className="text-4xl font-bold text-[#005E6E]">{location.siteName}</h2>
        
        {/* Last Updated and Badge - Under site name (since refresh button is gone) */}
        <div className="flex items-center mt-1 space-x-2">
          <p className="text-xs text-[#5D5346]">
            {isLoading ? 'Loading data...' : `Last updated: ${lastUpdated}`}
          </p>
          <span className={`px-2 py-0.5 ${badge.bg} ${badge.text} rounded-full text-xs flex items-center`}>
            {badge.isLive && (
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1.5"></span>
            )}
            {badge.label}
          </span>
        </div>

        {/* Site Description - Under Last Updated info */}
        <p className="text-sm text-[#5D5346] mt-2">{location.siteDescription}</p>
        
        {error && (
          <div className="mt-2 p-3 bg-red-50 border border-red-100 rounded-md">
            <p className="text-sm text-red-600">
              {error}
            </p>
            <button 
              onClick={() => setRetryCount(prev => prev + 1)} // Use setRetryCount to trigger retry
              className="text-xs text-[#005E6E] underline mt-1"
            >
              Retry
            </button>
          </div>
        )}
        
        {DEBUG_MODE && dataSource === 'api' && Object.keys(sensorData || {}).length === 0 && !error && !isLoading && (
          <div className="mt-2 p-3 bg-amber-50 border border-amber-100 rounded-md">
            <p className="text-sm text-amber-600">
              API returned no data. Consider enabling USE_FALLBACK_DATA to show mock data.
            </p>
          </div>
        )}
      </div>

      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {/* Static coordinate parameters */}
          {coordinateParameters.map((param) => (
            <ParameterCard
              key={param.label}
              {...param}
              onClick={() => onParameterClick(param)}
            />
          ))}

          {/* Dynamic sensor parameters - each card shows the average value */}
          {sensorData && Object.keys(sensorData).length > 0 ? (
            Object.entries(sensorData)
              .sort(([, a], [, b]) => {
                // Move items starting with "0" to the bottom
                const aIsZero = a.latestValue?.startsWith('0');
                const bIsZero = b.latestValue?.startsWith('0');
                if (aIsZero && !bIsZero) return 1;
                if (!aIsZero && bIsZero) return -1;
                return 0;
              })
              .map(([key, data]) => {
              console.log(`🎨 Rendering card for [${key}], value: ${data.latestValue}`);
              // Get the chart data for this sensor
              const sensorChartData = prepareChartData(data.readings);
              
              // Format description to show this is an average if we have multiple readings
              const readingsCount = data.readings.length;
              const description = readingsCount > 1 
                ? `${key.charAt(0) + key.slice(1).toLowerCase()} (avg of ${readingsCount} readings)`
                : `${key.charAt(0) + key.slice(1).toLowerCase()} reading`;
              
              return (
                <ParameterCard
                  key={key}
                  label={key}
                  value={data.latestValue} // This is already the average with unit
                  description={description}
                  chartData={sensorChartData}
                  onClick={() => onParameterClick({
                    label: key,
                    value: data.latestValue,
                    description: description,
                    chartData: sensorChartData
                  })}
                />
              );
            })
          ) : !error ? (
            <div className="col-span-2 text-center py-6">
              <p className="text-[#5D5346]">No sensor data available for this location.</p>
              <button 
                onClick={() => setRetryCount(prev => prev + 1)} // Use setRetryCount to trigger retry
                className="mt-2 px-4 py-1 bg-[#005E6E] text-white text-sm rounded-md"
              >
                Retry
              </button>
            </div>
          ) : null}

          {/* Temperature Chart Card - shown only if we have temperature data */}
          {temperatureChartData.length > 0 && (
            <div className="col-span-2">
              <ParameterCard
                label="TEMPERATURE TREND"
                value={sensorData?.TEMPERATURE?.latestValue || "N/A"}
                description="Recent temperature readings"
                onClick={() => onParameterClick({
                  label: "TEMPERATURE TREND",
                  value: sensorData?.TEMPERATURE?.latestValue || null,
                  description: "Recent temperature readings",
                  chartData: temperatureChartData
                })}
              >
                <div className="h-48 mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={temperatureChartData}>
                      <XAxis 
                        dataKey="time" 
                        stroke="#5D5346" 
                        fontSize={10}
                        tickLine={false}
                      />
                      <YAxis 
                        stroke="#5D5346" 
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => `${value}°C`}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#005E6E" 
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </ParameterCard>
            </div>
          )}
        </div>
      )}
    </div>
  );
} 